<template>
  <v-dialog v-model="dialog" persistent max-width="760">
    <v-card>
      <v-card-title class="kb-modal-title">
        <h3>Importar perguntas via JSON</h3>
        <v-btn icon @click="close">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="kb-modal-body">
        <div class="kb-form">
          <p class="text-gray-dark kb-import-help">
            Cole o JSON gerado (ou selecione um arquivo). As <strong>categorias</strong> e
            <strong>subcategorias</strong> referenciadas precisam já existir na Base de Conhecimento.
            As perguntas importadas entram como <strong>personalizadas</strong> desta campanha.
            A resposta aceita HTML.
          </p>

          <div class="kb-import-doc">
            <span>
              <i class="fal fa-robot mr-1"></i>
              Vai usar uma IA para montar o JSON? Baixe as instruções e entregue ao agente.
            </span>
            <v-btn flat round small class="btn px-3" @click="downloadInstructions">
              <i class="fal fa-download mr-2"></i>Baixar instruções (.md)
            </v-btn>
          </div>

          <div class="kb-field">
            <input
              type="file"
              ref="fileInput"
              accept=".json,application/json"
              style="display:none"
              @change="onFileSelected"
            />
            <v-btn flat round small class="btn px-3" @click="$refs.fileInput.click()">
              <i class="fal fa-file-upload mr-2"></i>Selecionar arquivo .json
            </v-btn>
          </div>

          <div class="kb-field">
            <label>JSON</label>
            <textarea
              v-model="jsonText"
              class="kb-json-textarea"
              rows="12"
              placeholder='{ "categorias": [ { "nome": "...", "subcategorias": [ { "nome": "...", "perguntas": [ { "pergunta": "...", "resposta": "<p>...</p>", "visivelAoDoador": true } ] } ] } ] }'
            ></textarea>
          </div>

          <div v-if="errors.length > 0" class="kb-import-errors">
            <strong>Não foi possível importar:</strong>
            <ul>
              <li v-for="(err, i) in errors" :key="i">{{ err }}</li>
            </ul>
          </div>

          <div v-if="preview && errors.length === 0" class="kb-import-preview">
            <i class="fal fa-check-circle mr-1"></i>
            {{ preview.count }} {{ preview.count === 1 ? 'pergunta pronta' : 'perguntas prontas' }}
            para importar em {{ preview.subcats }} {{ preview.subcats === 1 ? 'subcategoria' : 'subcategorias' }}.
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="kb-modal-actions">
        <v-btn flat round @click="close">Cancelar</v-btn>
        <v-btn flat round class="btn px-3" :disabled="!jsonText.trim()" @click="validate">
          Validar
        </v-btn>
        <v-btn
          flat
          round
          color="white"
          class="btn-primary px-4"
          :disabled="!canImport"
          @click="doImport"
        >
          Importar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script type="plain/text">
import CampaignService from "@/scripts/services/campaign.service";
import { FAQ_IMPORT_INSTRUCTIONS } from "@/scripts/helpers/faqImportInstructions";

export default {
  props: {
    categories: { type: Array, default: () => [] },
    campaignId: { type: Number, default: 0 }
  },
  data: () => ({
    dialog: false,
    jsonText: "",
    errors: [],
    preview: null,
    importing: false,
    campaignService: new CampaignService()
  }),
  computed: {
    canImport() {
      return this.preview && this.preview.count > 0 && this.errors.length === 0 && !this.importing;
    }
  },
  methods: {
    open() {
      this.jsonText = "";
      this.errors = [];
      this.preview = null;
      this.importing = false;
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    downloadInstructions() {
      const blob = new Blob([FAQ_IMPORT_INSTRUCTIONS], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "IMPORT_FAQ_JSON.md";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = e => {
        this.jsonText = e.target.result;
        this.validate();
      };
      reader.readAsText(file);
      event.target.value = "";
    },
    findByName(list, name) {
      const normalized = (name || "").trim().toLowerCase();
      return (list || []).find(x => (x.name || "").trim().toLowerCase() === normalized);
    },
    // Faz o parse + resolve categorias/subcategorias por nome, retornando { list, errors, subcats }.
    build() {
      const result = { list: [], errors: [], subcats: 0 };
      let data;
      try {
        data = JSON.parse(this.jsonText);
      } catch (e) {
        result.errors.push("JSON inválido: " + e.message);
        return result;
      }

      const categorias = data && data.categorias;
      if (!Array.isArray(categorias) || categorias.length === 0) {
        result.errors.push('JSON sem a lista "categorias".');
        return result;
      }

      const usedSubcats = {};
      categorias.forEach(cat => {
        const category = this.findByName(this.categories, cat.nome);
        if (!category) {
          result.errors.push(`Categoria não encontrada: "${cat.nome}". Crie-a na Base de Conhecimento primeiro.`);
          return;
        }
        (cat.subcategorias || []).forEach(sub => {
          const subcategory = this.findByName(category.subcategories || [], sub.nome);
          if (!subcategory) {
            result.errors.push(`Subcategoria não encontrada em "${cat.nome}": "${sub.nome}". Crie-a na Base de Conhecimento primeiro.`);
            return;
          }
          (sub.perguntas || []).forEach(q => {
            if (!q.pergunta || !q.resposta) {
              result.errors.push(`Pergunta ou resposta vazia em "${cat.nome} > ${sub.nome}".`);
              return;
            }
            usedSubcats[subcategory.id] = true;
            result.list.push({
              id: 0,
              campaignId: this.campaignId,
              question: q.pergunta,
              answer: q.resposta,
              subcategoryId: subcategory.id,
              isVisibleToDonor: q.visivelAoDoador !== false
            });
          });
        });
      });

      result.subcats = Object.keys(usedSubcats).length;
      return result;
    },
    validate() {
      const result = this.build();
      this.errors = result.errors;
      this.preview = result.errors.length === 0 ? { count: result.list.length, subcats: result.subcats } : null;
      if (result.errors.length === 0 && result.list.length === 0) {
        this.errors = ["Nenhuma pergunta encontrada no JSON."];
      }
    },
    doImport() {
      const result = this.build();
      if (result.errors.length > 0 || result.list.length === 0) {
        this.validate();
        return;
      }
      this.importing = true;
      this.campaignService.saveFaq(() => {
        this.importing = false;
        this.$emit("imported", result.list.length);
        this.close();
      }, result.list);
    }
  }
};
</script>

<style scoped>
.kb-modal-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.kb-modal-body {
  padding: 0 !important;
}
.kb-form {
  width: 100%;
  box-sizing: border-box;
  padding: 24px 32px;
}
.kb-field {
  margin-bottom: 12px;
}
.kb-field > label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #555;
}
.kb-import-help {
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 12px;
}
.kb-import-doc {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f7f8fb;
  border: 1px dashed #cfd2dd;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #555;
}
.kb-json-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: "Courier New", monospace;
  font-size: 12px;
  resize: vertical;
  outline: none;
}
.kb-import-errors {
  background: #fff3cd;
  color: #856404;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
}
.kb-import-errors ul {
  margin: 6px 0 0;
  padding-left: 18px;
}
.kb-import-preview {
  background: #d4edda;
  color: #155724;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
}
.kb-modal-actions {
  justify-content: flex-end;
  padding: 16px 24px;
}
</style>
